import { Download, Printer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FieldLabel, PageHead, PageToolbar, PendingDecision, SectionNav, ToolbarIconButton } from '@/components/editorial';
import { FilterChips } from '@/components/common/FilterChips';
import { NAV_KEY } from '@/i18n/keys/nav';
import { cn } from '@/lib/utils';
import { COARSE_HIT_ALONE } from '@/lib/touch-target';
import {
  MEDICAL_SECTIONS,
  SYMPTOM_SEVERITY_LABEL_KEY,
  isFiltered,
  type MedScope,
  type MedicalFilters,
  type MedicalSection,
  type QuestionScope,
  type SeverityScope,
  type SymptomScope,
} from '@/lib/medical-display';

// One create-action label per section — the `+` menu lists all three so none
// of them requires switching tab first (#848).
const ADD_LABEL_KEY = {
  medications: 'health:chrome.add.medications',
  questions:   'health:chrome.add.questions',
  symptoms:    'health:chrome.add.symptoms',
} as const satisfies Record<MedicalSection, string>;

const SEARCH_PLACEHOLDER_KEY = {
  medications: 'health:chrome.search.medications',
  questions:   'health:chrome.search.questions',
  symptoms:    'health:chrome.search.symptoms',
} as const satisfies Record<MedicalSection, string>;

// "Filter the …" is a whole sentence per section, never a lower-cased label
// spliced into English (#2109 R3 L3).
const FILTER_LABEL_KEY = {
  medications: 'health:chrome.filter.medications',
  questions:   'health:chrome.filter.questions',
  symptoms:    'health:chrome.filter.symptoms',
} as const satisfies Record<MedicalSection, string>;

// ── Header ────────────────────────────────────────────────────────────────

interface MedicalHeaderProps {
  section: MedicalSection;
  onSectionChange: (s: MedicalSection) => void;
  filters: MedicalFilters;
  onFiltersChange: (f: MedicalFilters) => void;
  search: string;
  onSearchChange: (q: string) => void;
  /** Open the create dialog for any section, switching the tab to it first. */
  onAddFor: (s: MedicalSection) => void;
  onPrint: () => void;
  /** CSV export of the symptom log; the icon renders on that section only. */
  onExport?: () => void;
  isParent?: boolean;
}

/**
 * Eyebrow → underline tabs → icon toolbar. No headline (DECISIONS 2026-09-10
 * "The headline goes"): the sidebar entry already says Health, the tab already
 * says which list this is, and the serif line under them restated both. The
 * eyebrow is the page's `<h1>` (PageHead without `title`). Nothing in the
 * header is a number — the count pips, tab counts and alert dots that used to
 * live here all restated the list underneath.
 */
export function MedicalHeader({
  section,
  onSectionChange,
  filters,
  onFiltersChange,
  search,
  onSearchChange,
  onAddFor,
  onPrint,
  onExport,
  isParent = true,
}: MedicalHeaderProps) {
  const { t } = useTranslation(['health', 'common']);
  return (
    <header className="space-y-4 pb-3.5 border-b border-border/60">
      {/* Eyebrow is "Health", matching the sidebar's Care > Health entry
          (design-grammar §4.4); it is the H1 and the document title. It is
          the nav row's own key (R3-B23), so the two cannot drift. */}
      <PageHead
        eyebrowKey={NAV_KEY.health}
        tabs={
          <SectionNav
            variant="underline"
            value={section}
            onValueChange={onSectionChange}
            items={MEDICAL_SECTIONS.map((s) => ({ value: s.value, label: t(s.labelKey) }))}
          />
        }
      />
      <MedicalToolbar
        section={section}
        filters={filters}
        onFiltersChange={onFiltersChange}
        search={search}
        onSearchChange={onSearchChange}
        onAddFor={onAddFor}
        onPrint={onPrint}
        onExport={onExport}
        isParent={isParent}
      />
    </header>
  );
}

// ── Toolbar ───────────────────────────────────────────────────────────────

type MedicalToolbarProps = Omit<MedicalHeaderProps, 'onSectionChange'>;

/**
 * Rendered through the shared `PageToolbar` (2026-09-10): + · search · filter ·
 * separator · print · export. The primitive owns the search field's open/clear
 * behaviour and the filter dot; this component only says what the tools are.
 */
export function MedicalToolbar({
  section,
  filters,
  onFiltersChange,
  search,
  onSearchChange,
  onAddFor,
  onPrint,
  onExport,
  isParent = true,
}: MedicalToolbarProps) {
  const { t } = useTranslation(['health', 'common']);
  const sectionLabel = t(MEDICAL_SECTIONS.find((s) => s.value === section)?.labelKey ?? 'health:section.medications');
  return (
    <PageToolbar
      label={sectionLabel}
      add={isParent ? {
        items: MEDICAL_SECTIONS.map((s) => ({ label: t(ADD_LABEL_KEY[s.value]), onSelect: () => onAddFor(s.value) })),
      } : undefined}
      search={{ value: search, onChange: onSearchChange, placeholder: t(SEARCH_PLACEHOLDER_KEY[section]) }}
      filter={{
        active: isFiltered(section, filters),
        label: t(FILTER_LABEL_KEY[section]),
        panel: <FilterPanel section={section} filters={filters} onChange={onFiltersChange} />,
      }}
    >
      {/* #2603 (D-2420): Print draws 32 px and reaches 44 px on a coarse
          pointer. It sits after the toolbar separator (9 px), so its 6 px
          reach never covers the Filter button. Export, when present, keeps
          its 32 px width and only reaches vertically, so neither steals the
          other's edge (its width is #2494's toolbar follow-up). */}
      <ToolbarIconButton
        label={t('health:chrome.print')}
        onClick={onPrint}
        className={cn(COARSE_HIT_ALONE, '[@media(pointer:coarse)]:after:-inset-1.5')}
      >
        <Printer className="h-4 w-4" />
      </ToolbarIconButton>
      {section === 'symptoms' && onExport && (
        <>
          <ToolbarIconButton
            label={t('health:chrome.export')}
            onClick={onExport}
            className={cn(COARSE_HIT_ALONE, '[@media(pointer:coarse)]:after:-inset-y-1.5')}
          >
            <Download className="h-4 w-4" />
          </ToolbarIconButton>
          {/* The export follows the UI language (R3-B19); the care team may read English. */}
          <PendingDecision id="symptom-csv-language" side="bottom" />
        </>
      )}
    </PageToolbar>
  );
}

// ── Filter panel (inside the popover) ─────────────────────────────────────

function FilterPanel({
  section,
  filters,
  onChange,
}: {
  section: MedicalSection;
  filters: MedicalFilters;
  onChange: (f: MedicalFilters) => void;
}) {
  const { t } = useTranslation(['health', 'common']);
  if (section === 'medications') {
    return (
      <div className="space-y-2">
        <FieldLabel>{t('health:chrome.show')}</FieldLabel>
        <FilterChips
          value={filters.medScope}
          onChange={(v) => onChange({ ...filters, medScope: v as MedScope })}
          options={[
            { value: 'active',   label: t('health:chrome.scope.active') },
            { value: 'inactive', label: t('health:chrome.scope.inactive') },
          ]}
        />
      </div>
    );
  }
  if (section === 'questions') {
    return (
      <div className="space-y-2">
        <FieldLabel>{t('health:chrome.show')}</FieldLabel>
        <FilterChips
          value={filters.qScope}
          onChange={(v) => onChange({ ...filters, qScope: v as QuestionScope })}
          options={[
            { value: 'pending',  label: t('health:chrome.scope.pending') },
            { value: 'answered', label: t('health:chrome.scope.answered') },
          ]}
        />
      </div>
    );
  }
  return (
    <>
      <div className="space-y-2">
        <FieldLabel>{t('health:chrome.show')}</FieldLabel>
        <FilterChips
          value={filters.symScope}
          onChange={(v) => onChange({ ...filters, symScope: v as SymptomScope })}
          options={[
            { value: 'all',       label: t('health:chrome.scope.all') },
            { value: 'notable',   label: t('health:chrome.scope.notable') },
            { value: 'reactions', label: t('health:chrome.scope.reactions') },
          ]}
        />
      </div>
      <div className="space-y-2">
        <FieldLabel>{t('health:chrome.severity')}</FieldLabel>
        <FilterChips
          value={filters.severity}
          onChange={(v) => onChange({ ...filters, severity: v as SeverityScope })}
          options={[
            { value: 'all',      label: t('health:chrome.scope.anySeverity') },
            { value: 'mild',     label: t(SYMPTOM_SEVERITY_LABEL_KEY.mild) },
            { value: 'moderate', label: t(SYMPTOM_SEVERITY_LABEL_KEY.moderate) },
            { value: 'severe',   label: t(SYMPTOM_SEVERITY_LABEL_KEY.severe) },
          ]}
        />
      </div>
    </>
  );
}
